import { useState, useEffect, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import HeroSection from '@/components/HeroSection';
import Leaderboard from '@/components/Leaderboard';
import RealtimeTicker from '@/components/RealtimeTicker';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import CurrencySwitcher from '@/components/CurrencySwitcher';
import { useDonations } from '@/hooks/useDonations';

// Lazy load modal - only needed when user clicks donate
const DonationModal = lazy(() => import('@/components/DonationModal'));

const Index = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    donations,
    totalAmount,
    loading,
    latestDonation,
    addDonation,
    getHighestAllTime,
    getMonthlyHighest,
    getNewestDonation,
  } = useDonations();

  // Handle ?open=donate query parameter
  useEffect(() => {
    if (searchParams.get('open') === 'donate') {
      setIsModalOpen(true);
      // Clean up the URL
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);


  return (
    <div className="min-h-screen bg-background pb-16">
      {/* Header with Switchers */}
      <header role="banner" className="fixed top-0 right-0 z-50 flex items-center gap-2 p-3 sm:p-4 bg-background/95 backdrop-blur-sm">
        <CurrencySwitcher />
        <LanguageSwitcher />
      </header>

      {/* Scanline overlay effect */}
      <div 
        className="pointer-events-none fixed inset-0 z-50 opacity-[0.03]"
        style={{
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, hsl(120 100% 55% / 0.1) 2px, hsl(120 100% 55% / 0.1) 4px)'
        }}
        aria-hidden="true"
      />

      {/* Main Content */}
      <main className="relative z-10" role="main">
        <HeroSection 
          donations={donations}
          onDonateClick={() => setIsModalOpen(true)}
          highestDonation={getHighestAllTime()}
        />
        
        <article aria-label={t('leaderboard.title')}>
          <Leaderboard
            donations={donations}
            loading={loading}
            highestAllTime={getHighestAllTime()}
            monthlyHighest={getMonthlyHighest()}
            newestDonation={getNewestDonation()}
          />
        </article>

        {/* Footer Credits */}
        <footer className="text-center py-8 text-xs text-muted-foreground" role="contentinfo">
          <p>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
          <p className="mt-1">{t('footer.artProject')}</p>
          <div className="mt-4 text-primary text-2xl font-mono tracking-widest opacity-60" aria-label="DUMONGO Logo">
            [ DM ]
          </div>
          {/* Hidden keywords for SEO */}
          <span className="sr-only">
            The Experiment. Join the Mongos. Nothing is Everything. The Leaderboard is watching.
            Invest in nothing. Become a legend. The dumbest highscore list on the internet.
          </span>
        </footer>
      </main>

      {/* Donation Modal - Lazy loaded */}
      <Suspense fallback={null}>
        <DonationModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
        />
      </Suspense>

      {/* Realtime Ticker */}
      <RealtimeTicker 
        donations={donations} 
        latestDonation={latestDonation} 
      />
    </div>
  );
};

export default Index;
